import { Injectable, Signal, inject } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { Store } from '@ngrx/store';
import { CommunityItem, CommunityTabType, FeedParams, PostExtendOptions } from '../../interfaces';
import { PostExtenderService } from '../../services/util/post-extender.service';
import * as FeedActions from './feed.actions';
import {
  selectTabItems,
  selectTabLoading,
  selectTabHasMore,
  selectTabScrollPosition,
  selectTabData,
  selectLastViewedPost,
  selectTargetPost,
  selectLastActiveTab,
} from './feed.selectors';
import { FeedTabData } from './feed.state';

@Injectable({ providedIn: 'root' })
export class FeedFacade {
  private store = inject(Store);
  private postExtenderService = inject(PostExtenderService);
  private refreshSubject = new Subject<void>();
  refreshTrigger$ = this.refreshSubject.asObservable();

  // Selectors
  selectTabItems(tabId: CommunityTabType): Signal<CommunityItem[]> {
    return this.store.selectSignal(selectTabItems(tabId));
  }

  selectTabLoading(tabId: CommunityTabType): Signal<boolean> {
    return this.store.selectSignal(selectTabLoading(tabId));
  }

  selectTabHasMore(tabId: CommunityTabType): Signal<boolean> {
    return this.store.selectSignal(selectTabHasMore(tabId));
  }

  selectTabScrollPosition(tabId: CommunityTabType): Signal<number> {
    return this.store.selectSignal(selectTabScrollPosition(tabId));
  }

  selectTabData(tabId: CommunityTabType): Signal<FeedTabData | null> {
    return this.store.selectSignal(selectTabData(tabId));
  }

  selectLastViewedPost(tabId: CommunityTabType): Signal<string | undefined> {
    return this.store.selectSignal(selectLastViewedPost(tabId));
  }

  selectTargetPost(tabId: CommunityTabType): Signal<string | undefined> {
    return this.store.selectSignal(selectTargetPost(tabId));
  }

  selectLastActiveTab(): Signal<CommunityTabType | undefined> {
    return this.store.selectSignal(selectLastActiveTab);
  }

  // Actions
  loadTabItems(
    tabId: CommunityTabType,
    userId: string,
    fetchParams: FeedParams,
    options: { isFirstLoad?: boolean; skipLikes?: boolean } = {},
  ): void {
    this.store.dispatch(FeedActions.loadTabItems({ tabId, userId, fetchParams, ...options }));
  }

  setTabScrollPosition(tabId: CommunityTabType, position: number, documentHeight?: number): void {
    this.store.dispatch(FeedActions.setTabScrollPosition({ tabId, position, documentHeight }));
  }

  clearTabData(tabId: CommunityTabType): void {
    this.store.dispatch(FeedActions.clearTabData({ tabId }));
  }

  clearAllTabs(): void {
    this.store.dispatch(FeedActions.clearAllTabs());
  }

  triggerRefresh(): void {
    this.clearAllTabs();
    this.refreshSubject.next();
  }

  deletePostFromTab(postId: string, tabId?: CommunityTabType): void {
    this.store.dispatch(FeedActions.deletePostFromTab({ postId, tabId }));
  }

  setLastViewedPost(tabId: CommunityTabType, postId: string): void {
    this.store.dispatch(FeedActions.setLastViewedPost({ tabId, postId }));
  }

  setTargetPost(tabId: CommunityTabType, postId: string): void {
    this.store.dispatch(FeedActions.setTargetPost({ tabId, postId }));
  }

  clearTargetPost(tabId: CommunityTabType): void {
    this.store.dispatch(FeedActions.clearTargetPost({ tabId }));
  }

  setLastActiveTab(tabId: CommunityTabType): void {
    this.store.dispatch(FeedActions.setLastActiveTab({ tabId }));
  }

  clearLastActiveTab(): void {
    this.store.dispatch(FeedActions.clearLastActiveTab());
  }

  extendPosts(posts: CommunityItem[], options?: PostExtendOptions): Observable<CommunityItem[]> {
    return this.postExtenderService.extendPosts(posts, options);
  }

  updatePostSavedStatus(postUuid: string, isSaved: boolean): void {
    this.store.dispatch(FeedActions.updatePostItem({
      postUuid,
      changes: { is_saved: isSaved },
    }));
  }
}
